/**
 * ADR 0418 D5/D7 — turn a `BrandState` (brand-state.ts) into what the screens and the browser
 * tab show. Pure — `BrandProvider`/`BrandHead`/`useAppBranding` only wire it to React and the DOM.
 * plan: 4-Archives/tms/deeptrack-plan/0418-one-web-brand-by-hostname.md
 *
 * Layering: the bundle's own `resolveBranding()` (app-org.ts, build-time env) supplies BEHAVIOUR
 * (subtitle, card heading, Powered by, org-bar default). IDENTITY — the name and the logo — comes
 * only from the API's brand for this host; when there is none it is neutral (D16), never the env's.
 */
import type { AppBranding } from "./app-org";
import type { BrandState } from "./brand-state";
import { DEFAULT_PLATFORM_NAME, isNoRedirectHost } from "@9nerd/shared";

/**
 * D16/D17 — the name (login page, sidebar AND tab) when no organisation is known: the API's
 * `platform_name` (env PLATFORM_NAME) when it answered, else the built-in "DeepTrack".
 * Never the bundle's env name.
 */
export function neutralName(state: BrandState): string {
  return state.kind === "neutral" && state.platformName ? state.platformName : DEFAULT_PLATFORM_NAME;
}
/** Neutral tab icon, served from `public/` (browsers also ask for `/favicon.ico` on their own). */
/**
 * The `?v=` busts the browser's favicon cache (browsers keep favicons for a very long time).
 * BUMP IT whenever `public/favicon.ico` changes — source: `apps/web/branding/platform-favicon.svg`
 * (blue "D", same mark as the login page's letter box · ADR 0418 D17).
 */
export const NEUTRAL_FAVICON_HREF = "/favicon.ico?v=platform-d1";

/**
 * ADR 0418 D15 — `localStorage[DEV_HOST_RULE_OFF_KEY] = "off"` turns the plain-localhost org-bar
 * rule off, so the e2e tests of ADR 0276 (org bar / switcher can be hidden) still prove something
 * on a dev server. Only ever read on plain localhost — on real domains the rule is off anyway.
 */
export const DEV_HOST_RULE_OFF_KEY = "tms:dev-host-rule";

/** Plain dev host (shared `isNoRedirectHost`) and the rule not switched off by a test. */
export function isPlainDevHostActive(host: string | null, ruleSwitch: string | null): boolean {
  return host !== null && isNoRedirectHost(host) && ruleSwitch !== "off";
}

export interface HostBranding extends AppBranding {
  /** true while the host's brand is still unknown — the login page reserves the space instead of showing a name. */
  brandLoading: boolean;
}

/**
 * ADR 0421 D3 — the login page's BEHAVIOUR too: the org's own choice (`login_otp`, `show_powered_by`
 * from `/public/brand`) beats the bundle's env; `null` (not set, or an old API) keeps the bundle's.
 * The ONLY place this is decided — the login page reads `showLoginOtp` / `showPoweredBy` from here.
 *
 *   state      showLoginOtp / showPoweredBy
 *   brand      brand value when boolean · else bundle
 *   neutral    bundle  (localhost, unknown host, API down without a cache)
 *   loading    false   (unknown yet — never flash the OTP switch and then pull it away)
 */
export function mergeHostBrand(base: AppBranding, state: BrandState): HostBranding {
  if (state.kind === "brand") {
    return {
      ...base,
      brandName: state.brand.login_title,
      logoSrc: state.brand.logo_url ?? "",
      showLoginOtp: state.brand.login_otp ?? base.showLoginOtp,
      showPoweredBy: state.brand.show_powered_by ?? base.showPoweredBy,
      brandLoading: false,
    };
  }
  if (state.kind === "neutral") {
    // D16 — no org owns this host (or we cannot tell): neutral name, no logo. The bundle's env
    // name/logo would be some org's identity (CCA today) shown on a page that is not theirs.
    return { ...base, brandName: neutralName(state), logoSrc: "", brandLoading: false };
  }
  return { ...base, showLoginOtp: false, showPoweredBy: false, brandLoading: true }; // loading · about to redirect
}

/**
 * ADR 0421 D4 — the admin form's three-way choice for a login behaviour flag ↔ the API value.
 * "default" = not set → `null` in the PATCH body (the API removes the field → bundle default).
 */
export type LoginFlagChoice = "default" | "on" | "off";
export function loginFlagChoice(v: unknown): LoginFlagChoice {
  return v === true ? "on" : v === false ? "off" : "default";
}
export function loginFlagValue(c: LoginFlagChoice): boolean | null {
  return c === "on" ? true : c === "off" ? false : null;
}

/** What `BrandHead` writes into `<head>`. */
export function headFor(state: BrandState): { title: string; faviconHref: string } {
  if (state.kind === "brand") {
    return {
      title: state.brand.browser_title,
      faviconHref: state.brand.favicon_url ?? NEUTRAL_FAVICON_HREF,
    };
  }
  return { title: neutralName(state), faviconHref: NEUTRAL_FAVICON_HREF };
}

/**
 * ADR 0418 D8 (web side) — is this page locked to one org, and which of the user's orgs is it?
 *
 * - `pending`: brand or the user's org list still loading — decide nothing yet
 * - `none`: no org owns this host (plain localhost, neutral, API down without cache) — old behaviour
 * - `locked`: the host belongs to `orgSlug`; `tenantId` is that org among the user's own orgs, or
 *   null when the user is not a member (→ wrong-domain notice, mockup frame 3)
 *
 * Matched by slug because the public brand never carries a tenant id (D4); `/organizations/mine`
 * gives the user's orgs with both.
 */
export type HostLock =
  | { status: "pending" }
  | { status: "none" }
  | { status: "locked"; orgSlug: string; orgName: string; tenantId: string | null };

export function hostLockFor(
  state: BrandState,
  myOrgs: readonly { tenant_id: string; org_slug: string }[] | null | undefined,
): HostLock {
  if (state.kind === "loading" || state.kind === "redirect") return { status: "pending" };
  if (state.kind !== "brand") return { status: "none" };
  if (!myOrgs) return { status: "pending" };
  const mine = myOrgs.find((o) => o.org_slug === state.brand.org_slug);
  return { status: "locked", orgSlug: state.brand.org_slug, orgName: state.brand.org_name, tenantId: mine?.tenant_id ?? null };
}
